import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";

import Seleccionartreball from "./Seleccionartreball";
import Detallstreball from "./Detallstreball";
import Ofertes from "./Ofertes";
import ResumTreball from "./ResumTreball";
import LogisticaTreball from "./LogisticaTreball";
import Tasques from "./Tasques";
import Agrupacions from "./Agrupacions";
import Organitzacio from "./Organitzacio";
import ContactesTreball from "./ContactesTreball";
import AjudaTreball from "./AjudaTreball";

import {
  haDObirNouAmbClient,
  obrirAltaClient,
  RUTA_TREBALLS,
} from "../../utils/AltaClientRetorn";

import "../dashboard_v2.css";
import "./treballs.css";

function idTreball(t) {
  return t ? Number(t.idTreballs) : null;
}

const SECCIONS_PENDENTS = {
  costos: "Costos — pendent d'implementar",
};

function PestanyesTreball({ active, onChange, pucModificar, pucAvancat, pucLogistica }) {
  const boto = (id, label, { disabled = false } = {}) => (
    <button
      type="button"
      className={`dv2-subnav-btn ${active === id ? "is-active" : ""}`}
      disabled={disabled}
      title={disabled ? SECCIONS_PENDENTS[id] : undefined}
      onClick={() => !disabled && onChange(id)}
    >
      {label}
    </button>
  );

  return (
    <nav className="dv2-subnav" aria-label="Seccions del treball">
      {boto("dades", "Dades")}
      {pucModificar && boto("tasques", "Tasques")}
      {pucModificar && boto("contactes", "Contactes")}
      {pucModificar && boto("ofertes", "Ofertes")}
      {pucAvancat && boto("costos", "Costos", { disabled: true })}
      {pucAvancat && boto("agrupacions", "Agrupacions")}
      {pucAvancat && boto("organitzacio", "Organització")}
      {pucModificar && boto("resum", "Resum")}
      {pucLogistica && boto("logistica", "Logística")}
    </nav>
  );
}

function Treballs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const obrirNouRetorn = haDObirNouAmbClient(location) && pucCrear;

  const [activeTab, setActiveTab] = useState(() => (obrirNouRetorn ? "nou" : "seleccionar"));
  const [selectedTreball, setSelectedTreball] = useState(null);
  const [treballs, setTreballs] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const client = location.state?.client || null;
  const clientId = client?.idClients || (typeof client === "number" ? client : null);

  const [filterText, setFilterText] = useState("");
  const [estatFilters, setEstatFilters] = useState(clientId ? "" : "Pendent,En curs");
  const [tipusTreballFilter, setTipusTreballFilter] = useState("");
  const [clientFilter, setClientFilter] = useState(clientId);

  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucAvancat = tincPermis(rolUsuari, "ACCES");
  const pucLogistica = Number(rolUsuari) >= 3;

  useEffect(() => {
    if (!location.state?.obrirNou) return;
    const { obrirNou, ...rest } = location.state;
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.pathname, location.state, navigate]);

  const tornarLlista = (refresca = false) => {
    setActiveTab("seleccionar");
    if (refresca) setReloadToken((n) => n + 1);
  };

  const handleTreballSelect = (treball) => {
    if (!treball) {
      setSelectedTreball(null);
      return;
    }
    setSelectedTreball((prev) => (idTreball(prev) === idTreball(treball) ? null : treball));
  };

  const obrirFitxa = (treball, mode) => {
    setSelectedTreball(treball);
    setActiveTab(mode);
  };

  const canviarSeccio = (seccio) => {
    if (seccio === "dades") {
      setActiveTab("consultar");
      return;
    }
    setActiveTab(seccio);
  };

  const refrescarTreball = useCallback(
    async (idT) => {
      if (!idT || !empresaSeleccionada?.IdEmpresa) return null;
      try {
        const resp = await fetch(`${API_URL}/Treballs.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            idTreballs: idT,
          }),
        });
        const data = await resp.json();
        if (data.success && data.treballs?.length) {
          const actualitzat = data.treballs[0];
          setSelectedTreball(actualitzat);
          setTreballs((prev) =>
            (prev || []).map((t) => (idTreball(t) === idTreball(actualitzat) ? { ...t, ...actualitzat } : t))
          );
          return actualitzat;
        }
      } catch (err) {
        console.error("Error refrescant treball:", err);
      }
      return null;
    },
    [empresaSeleccionada]
  );

  const esLlista = activeTab === "seleccionar";
  const tabEfectiu =
    !pucAvancat && (activeTab === "agrupacions" || activeTab === "organitzacio")
      ? "consultar"
      : !pucLogistica && activeTab === "logistica"
      ? "consultar"
      : activeTab;
  const mostraPestanyes = selectedTreball && !["nou", "esborrar"].includes(tabEfectiu);
  const seccioActiva =
    tabEfectiu === "tasques" ||
    tabEfectiu === "contactes" ||
    tabEfectiu === "ofertes" ||
    tabEfectiu === "agrupacions" ||
    tabEfectiu === "organitzacio" ||
    tabEfectiu === "resum" ||
    tabEfectiu === "logistica"
      ? tabEfectiu
      : "dades";
  const subnav = mostraPestanyes ? (
    <PestanyesTreball
      active={seccioActiva}
      onChange={canviarSeccio}
      pucModificar={pucModificar}
      pucAvancat={pucAvancat}
      pucLogistica={pucLogistica}
    />
  ) : null;

  const subtitolLlista = clientId
    ? "Treballs del client seleccionat."
    : "Obra, reparació o manteniment. La secció Costos encara no està feta.";

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaTreball pestanya="llista" onTancar={() => setMostraAjuda(false)} />}
      {esLlista ? (
        <>
          <div className="dv2-pageHeader">
            <div className="dv2-pageHeader-main">
              <div className="dv2-pageHeader-icon" aria-hidden="true">
                🛠️
              </div>
              <div>
                <h1 className="dv2-pageHeader-title">Treballs</h1>
                <p className="dv2-pageHeader-subtitle">{subtitolLlista}</p>
              </div>
            </div>
            <div className="dv2-pageHeader-actions">
              <button
                type="button"
                className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                title="Com funcionen els Treballs"
                aria-label="Ajuda"
                onClick={() => setMostraAjuda(true)}
              >
                ?
              </button>
              {pucCrear && (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--primary"
                  onClick={() => {
                    setSelectedTreball(null);
                    setActiveTab("nou");
                  }}
                >
                  Nou treball
                </button>
              )}
            </div>
          </div>

          <Seleccionartreball
            onTreballSelect={handleTreballSelect}
            onConsultar={(t) => obrirFitxa(t, "consultar")}
            onModificar={(t) => obrirFitxa(t, "modificar")}
            onEsborrar={(t) => obrirFitxa(t, "esborrar")}
            onTasques={(t) => obrirFitxa(t, "tasques")}
            onContactes={(t) => obrirFitxa(t, "contactes")}
            onOfertes={(t) => obrirFitxa(t, "ofertes")}
            onAgrupacions={pucAvancat ? (t) => obrirFitxa(t, "agrupacions") : undefined}
            onOrganitzacio={pucAvancat ? (t) => obrirFitxa(t, "organitzacio") : undefined}
            onResum={(t) => obrirFitxa(t, "resum")}
            onLogistica={pucLogistica ? (t) => obrirFitxa(t, "logistica") : undefined}
            selectedTreball={selectedTreball}
            idClient={client}
            onTreballsChange={setTreballs}
            reloadToken={reloadToken}
            filters={{
              filterText,
              setFilterText,
              estatFilters,
              setEstatFilters,
              tipusTreballFilter,
              setTipusTreballFilter,
              clientFilter,
              setClientFilter,
            }}
          />
        </>
      ) : tabEfectiu === "tasques" ? (
        <Tasques
          treball={selectedTreball}
          subnav={subnav}
          onCancel={() => tornarLlista(false)}
          onVeureLogistica={pucLogistica ? () => setActiveTab("logistica") : undefined}
        />
      ) : tabEfectiu === "logistica" ? (
        <LogisticaTreball treball={selectedTreball} subnav={subnav} onCancel={() => tornarLlista(false)} />
      ) : tabEfectiu === "contactes" ? (
        <ContactesTreball treball={selectedTreball} subnav={subnav} onCancel={() => tornarLlista(false)} />
      ) : tabEfectiu === "ofertes" ? (
        <Ofertes
          tipus="treball"
          entitat={selectedTreball}
          subnav={subnav}
          onCancel={() => tornarLlista(false)}
        />
      ) : tabEfectiu === "agrupacions" ? (
        <Agrupacions treball={selectedTreball} subnav={subnav} onCancel={() => tornarLlista(false)} />
      ) : tabEfectiu === "organitzacio" ? (
        <Organitzacio treball={selectedTreball} subnav={subnav} onCancel={() => tornarLlista(false)} />
      ) : tabEfectiu === "resum" ? (
        <ResumTreball treball={selectedTreball} subnav={subnav} onCancel={() => tornarLlista(false)} />
      ) : (
        <Detallstreball
          mode={tabEfectiu}
          treball={activeTab === "nou" ? null : selectedTreball}
          treballs={treballs}
          clientInicial={client}
          subnav={subnav}
          onNavigate={setSelectedTreball}
          onNouClient={
            pucCrear
              ? () =>
                  obrirAltaClient(navigate, {
                    tornarA: RUTA_TREBALLS,
                    enTornar: { obrirNou: true },
                  })
              : undefined
          }
          onCancel={() => tornarLlista(tabEfectiu === "nou" || tabEfectiu === "esborrar")}
          onCanviarMode={(modeNou) => setActiveTab(modeNou)}
          onSave={async (updated) => {
            if (activeTab === "nou") {
              tornarLlista(true);
              return;
            }
            const idT = updated?.idTreballs || selectedTreball?.idTreballs;
            const actualitzat = await refrescarTreball(idT);
            if (actualitzat) {
              setActiveTab("consultar");
              return;
            }
            if (updated) setSelectedTreball(updated);
            setActiveTab("consultar");
          }}
        />
      )}
    </div>
  );
}

export default Treballs;
